import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Utensils, Trophy, MessageSquare, Share2 } from "lucide-react";
import { toast } from "sonner";
import { canNativeShare, nativeShare, copyText } from "@/lib/share";
import { trackEvent } from "@/lib/analytics";
import { useLang } from "@/context/LanguageContext";
import { fmtNumber } from "@/lib/i18n";
import { KIND_LABEL } from "@/lib/recipe";
import LiveFood from "@/components/LiveFood";

const ITALY_KM2 = 302073;
const pick = (o, lang) => (o && (o[lang] || o.en)) || "";

function useCountUp(target, ms = 1800) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf; const t0 = performance.now();
    const tick = (now) => { const p = Math.min(1, (now - t0) / ms); setV(Math.round(target * (1 - Math.pow(1 - p, 3)))); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
}

const Kicker = ({ children }) => <div className="text-[11px] uppercase tracking-[0.3em] text-ochre font-semibold story-in drop-shadow-[0_1px_3px_rgba(0,0,0,.9)]">{children}</div>;
const Big = ({ children, className = "", ...rest }) => <div className={`font-display font-bold text-white leading-[0.95] story-in story-in-2 ${className}`} {...rest}>{children}</div>;
const Sub = ({ children }) => <p className="text-base md:text-lg text-white/85 leading-relaxed story-in story-in-3 max-w-md">{children}</p>;

function FlagSlide({ country, lang, t }) {
  return (
    <>
      <Kicker>{t("story_kicker")}</Kicker>
      <Big className="text-5xl sm:text-6xl">{country.names[lang]}</Big>
      <Sub><span dir="rtl">{country.names.ar}</span> · {pick(country.capital, lang)}</Sub>
    </>
  );
}

function PopulationSlide({ country, lang, t }) {
  const n = useCountUp(country.population);
  return (
    <>
      <Kicker>{t("population")}</Kicker>
      <Big className="text-5xl sm:text-7xl stat-number tabular-nums" data-testid="story-population">{fmtNumber(n, lang)}</Big>
    </>
  );
}

function AreaSlide({ country, lang, t }) {
  const ratio = country.area_km2 / ITALY_KM2;
  const cmp = ratio >= 1 ? t("story_times_italy").replace("{x}", fmtNumber(Math.round(ratio * 10) / 10, lang)) : t("story_pct_italy").replace("{x}", fmtNumber(Math.round(ratio * 100), lang));
  return (
    <>
      <Kicker>{t("area")}</Kicker>
      <Big className="text-5xl sm:text-7xl stat-number">{fmtNumber(country.area_km2, lang)} <span className="text-2xl text-white/70">{t("km2")}</span></Big>
      <Sub>{cmp}</Sub>
    </>
  );
}

function CitiesSlide({ country, lang, t }) {
  const places = (country.travel_guide?.places || []).slice(0, 3);
  return (
    <>
      <Kicker>{t("capital")}</Kicker>
      <Big className="text-5xl sm:text-6xl">{pick(country.capital, lang)}</Big>
      <div className="story-in story-in-3">
        <div className="text-[11px] uppercase tracking-[0.25em] text-white/60 mb-2">{t("story_cities")}</div>
        <ul className="space-y-1.5">{places.map((p) => <li key={p.wiki} className="text-lg text-white font-display">{pick(p.name, lang)}</li>)}</ul>
      </div>
    </>
  );
}

function FoodSlide({ country, lang, t }) {
  const c = country.travel_guide?.cuisine || {};
  return (
    <>
      <Kicker>{t("story_food")}</Kicker>
      <ul className="space-y-3 w-full max-w-sm">
        {["dish", "dessert", "street"].map((k, i) => c[k] && (
          <li key={k} className={`flex items-center gap-3 story-in story-in-${i + 1}`}>
            <LiveFood food={c[k]} kind={k} alt="" className="w-16 h-16 rounded-xl border border-white/20 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">{t(KIND_LABEL[k])}</div>
              <div className="font-display font-bold text-xl text-white leading-tight">{pick(c[k].name, lang)}</div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

function HistorySlide({ country, lang, t }) {
  return (
    <>
      <Kicker>{t("history")}</Kicker>
      <Big className="text-4xl sm:text-5xl" data-testid="story-history-title">{pick(country.independence, lang)}</Big>
    </>
  );
}

function TextSlide({ kicker, title, text }) {
  const head = title || (text || "").split(/[,.(;:]/)[0];
  return (
    <>
      <Kicker>{kicker}</Kicker>
      {head && <Big className="text-4xl sm:text-5xl">{head}</Big>}
    </>
  );
}

function EndSlide({ country, lang, t }) {
  const name = country.names[lang] || country.names.en;
  const shareStory = async () => {
    const url = `${window.location.origin}/api/share/${country.code}?lang=${lang}&story=1`;
    const text = t("story_share_text").replace("{name}", name);
    trackEvent("share", { method: "story", content_type: "story", item_id: country.code, lang });
    if (canNativeShare()) await nativeShare({ title: name, text, url });
    else { await copyText(`${text} ${url}`); toast.success(t("copied")); }
  };
  const Btn = ({ to, icon: Icon, label, testid }) => (
    <Link to={to} className="flex items-center gap-3 rounded-2xl bg-white/15 backdrop-blur px-4 py-3 text-white hover:bg-ochre transition-colors" data-testid={testid}><Icon className="w-5 h-5 text-ochre" /> <span className="font-semibold">{label}</span></Link>
  );
  return (
    <>
      <Kicker>{country.names[lang]}</Kicker>
      <Big className="text-4xl sm:text-5xl">{t("story_end_title")}</Big>
      <div className="grid grid-cols-2 gap-2 w-full max-w-sm story-in story-in-3">
        <Btn to={`/?c=${country.code}`} icon={BookOpen} label={t("story_end_guide")} testid="story-end-guide" />
        <Btn to={`/recipe/${country.code}/dish`} icon={Utensils} label={t("story_end_recipes")} testid="story-end-recipes" />
        <Btn to="/quiz" icon={Trophy} label={t("quiz")} testid="story-end-quiz" />
        <Btn to={`/?c=${country.code}#reviews`} icon={MessageSquare} label={t("reviews")} testid="story-end-reviews" />
      </div>
      <button onClick={shareStory} className="w-full max-w-sm inline-flex items-center justify-center gap-2 rounded-full bg-ochre text-white px-5 py-3 font-semibold hover:bg-white hover:text-ink transition-colors story-in story-in-3" data-testid="story-share-button">
        <Share2 className="w-4 h-4" /> {t("story_share")}
      </button>
    </>
  );
}

export default function StorySlide({ slideKey, country, facts }) {
  const { lang, t } = useLang();
  const p = { country, lang, t };
  switch (slideKey) {
    case "flag": return <FlagSlide {...p} />;
    case "population": return <PopulationSlide {...p} />;
    case "area": return <AreaSlide {...p} />;
    case "cities": return <CitiesSlide {...p} />;
    case "food": return <FoodSlide {...p} />;
    case "history": return <HistorySlide {...p} />;
    case "nature": return <TextSlide kicker={t("nature")} title={pick(country.nature?.fauna, lang).split(",")[0]} text={pick(facts?.curiosity, lang) || pick(country.nature?.fauna, lang)} />;
    case "currency": return <TextSlide kicker={t("currency")} title={pick(country.currency, lang)} text={pick(country.economy_base, lang)} />;
    case "languages": return <TextSlide kicker={t("story_languages")} text={pick(facts?.languages, lang)} />;
    case "climate": return <TextSlide kicker={t("story_climate")} title={pick(country.travel_guide?.best_time, lang)} text={pick(facts?.climate, lang)} />;
    default: return <EndSlide {...p} />;
  }
}
